import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  GET_DEPARTMENT_CONTACTS, MANIFEST_API_PATH, VERSION_API_PATH, WHITELABEL_CONFIG_API_PATH
} from './constants/routes'
import { RTL_LANGUAGES } from './constants/constants'
import { useStore } from './StateProvider'
import { isEmpty, isNil } from 'ramda'
import { saveState } from './utils/storage'
import DataProvider from './utils/provider'
import { createStoreValidation } from './components/ExternalFields'
import { setDefaultLang } from './i18n/i18nUtils'
import { RouterComponent } from './RouterComponent'
import queryString from 'query-string'
import {
  baseFldsDescriptionListTier1,
  baseFldsDescriptionListTier2
} from './components/baseFieldsConfig'
import { fetchCitizenshipCountries, fetchCobCountries, fetchCountries, processPromotional } from './components/api'
import { useTranslation } from 'react-i18next'
import HeadTags from './components/pageParts/HeadTags'
import Header from './components/pageParts/Header/Header'
import Footer from './components/pageParts/Footer/Footer'
import styles from './WhitelabelContainer.css'
import { loadGoogleAnalytics } from './components/utils/googleTag'

const CONFIG_LIFE_TIME = 5 * 60 * 1000

/**
 * @typedef Step
 * @type {object}
 * @property {{string: string}} i18n
 * @property {number} id
 * @property {number} tier
 */

/**
 * @typedef BaseFieldsOrder
 * @type {object}
 * @property {string} name
 * @property {number} order
 * @property {?number} step
 * @property {number} tier
 */

/**
 * @typedef AdvancedConfig
 * @type {object}
 * @property {string} defaultCountry
 * @property {ExternalField[]} extFields
 * @property {{string: string}[]} customTexts
 * @property {string[]} excludedLanguages
 * @property {boolean} autoLoginEnabled
 * @property {boolean} immediateLoginEnabled
 * @property {boolean} redirectToTier2FormAfterCreate
 * @property {boolean} moveToUpgradeForm
 * @property {number} questionnaireTier
 * @property {BaseFieldsOrder[]} baseFieldsOrder
 * @property {Step[]} steps
 * @property {string} theme
 * @property {boolean} logoLinkDisabled
 */

/**
 * @typedef WhitelabelConfigCommon
 * @type {object}
 * @property {string} email
 * @property {string} emailSenderAddress
 * @property {string} loginURL
 * @property {string} logo
 * @property {string} mimeType
 * @property {string} phone
 * @property {boolean} showDemoButton
 * @property {string} whiteLabelName
 * @property {string} whiteLabelURL
 * @property {string} privacyPolicyURL
 * @property {number} timestamp
 * @property {object} error
 * @property {string} targetPlatform
 * @property {string} favicon
 * @property {boolean} mobileVerificationEnabled
 */

/**
 * @typedef WLAdvancedConfigFld
 * @type {object}
 * @property {AdvancedConfig} advancedConfig
 * */

/**
 * @typedef SelectItem
 * @type {object}
 * @property {string} value
 * @property {string} label
 */

/**
 * @typedef DepartmentInfo
 * @type {object}
 * @property {string} code
 * @property {string} emailSenderAddress
 * @property {string} phone
 * @property {string} site
 * @property {string} email
 */

/**
 * @param {number} timestamp
 * @returns {boolean}
 */
const shouldFetchConfig = (timestamp) =>
  isEmpty(localStorage.getItem('whitelabelConfig')) || isTimeElapsed(timestamp)

/**
 *
 * @param {number} timestamp
 * @param {number} now
 * @returns {boolean}
 */
const isTimeElapsed = (timestamp, now = new Date().getTime()) =>
  now - timestamp > CONFIG_LIFE_TIME

/**
 * @param {WhitelabelConfigCommon & AdvancedConfig} config
 */
const saveWlToStore = (config) =>
  saveState(
    {
      ...config,
      timestamp: new Date().getTime()
    },
    'whitelabelConfig'
  )
/**
 *
 * @param {WhitelabelConfigCommon & WLAdvancedConfigFld} whitelabel
 * @returns {WhitelabelConfigCommon & AdvancedConfig}
 */
const convertWlToStore = (whitelabel) => {
  const baseFieldsDescription = [...baseFldsDescriptionListTier1, ...baseFldsDescriptionListTier2]
  const baseFieldsFullDescription = baseFieldsDescription.map((fldDescription) => {
    const fldOrderDescription = whitelabel.advancedConfig?.baseFieldsOrder.find(
      ({ name }) => name === fldDescription.name
    )
    return { ...fldDescription, ...fldOrderDescription }
  })
  const state = {
    ...whitelabel,
    ...whitelabel.advancedConfig,
    extFields: [...baseFieldsFullDescription, ...whitelabel.advancedConfig?.extFields]
  }
  delete state.advancedConfig
  return state
}

const setTheme = (theme) => {
  if (theme) {
    document.getElementsByTagName('body')[0].classList.add(theme)
  }
}

const getDepartmentContacts = ({ utmCampaign, deptCode, fundId, brokerGroup }) => {
  let params = ''
  if (utmCampaign?.length) {
    params = `${params}utmCampaign=${utmCampaign}`
  } else if (deptCode?.length) {
    params = `${params}deptCode=${deptCode}`
  } else if (fundId?.length) {
    params = `${params}fundId=${fundId}`
  } else if (brokerGroup?.length) {
    params = `${params}brokerGroup=${brokerGroup}`
  }
  params = params.length ? `?${params}` : params
  return DataProvider.get(`${GET_DEPARTMENT_CONTACTS}${params}`)
}

/**
 * @param {{ version: string}} params
 */
const onVersionResolve = ({ version }) => {
  console.log(`backend build version: ${version}`)
  return version
}

export const WhitelabelContainer = () => {
  const [isInitialised, setIsInitialised] = useState(false)
  const [{ whitelabel, validation, data: { countries } }, dispatch] = useStore()
  const location = useLocation()
  const { i18n } = useTranslation()

  useEffect(() => {
    const { lang, theme: urlTheme } = queryString.parse(location.search)

    DataProvider.get(VERSION_API_PATH)
      .then(onVersionResolve)
      .then(fetchWL)
      .then((wl) => {
        const stateExtField = wl.extFields.find((field) => field.name === 'state')
        if (stateExtField) {
          stateExtField.type = 'state'
        }

        if (wl.googleTagId) {
          loadGoogleAnalytics(wl.googleTagId)
        }

        setDefaultLang(wl.excludedLanguages, lang)
        dispatchSetValidation(wl.extFields, wl.mobileVerificationEnabled)
        setTheme(wl.theme || urlTheme)
        dispatch({ type: 'setWL', whitelabel: wl })
        saveWlToStore(wl)
        setIsInitialised(true)
      })
      .then(() => {
        const { extRefId } = queryString.parse(document.location.search)
        if (extRefId && typeof extRefId === 'string' && extRefId.length > 0) {
          processPromotional(extRefId)
        }
      })
  }, [])

  useEffect(() => {
    if (i18n.language) {
      document.documentElement.setAttribute('lang', i18n.language)
      if (RTL_LANGUAGES.includes(i18n.language)) {
        document.documentElement.setAttribute('dir', 'rtl')
      } else {
        document.documentElement.setAttribute('dir', 'ltr')
      }
      const oldLink = document.querySelector('link[rel="manifest"]')
      if (oldLink) {
        oldLink.remove()
      }
      const link = document.createElement('link')
      link.setAttribute('rel', 'manifest')
      link.setAttribute('href', `${MANIFEST_API_PATH}?lang=${i18n.language}`)
      document.head.append(link)
    }
  }, [i18n.language])

  useEffect(() => {
    if (!isInitialised) return
    /**
     * @param {Country[]} countriesRaw
     */
    const fetchCountriesCallback = (countriesRaw, propertyName) => {
      dispatch({ type: propertyName, countries: countriesRaw })
    }
    if (i18n.language) {
      fetchCountries().then(response => fetchCountriesCallback(response, 'setCountries'))
      fetchCobCountries().then(response => fetchCountriesCallback(response, 'setCobCountries'))
      fetchCitizenshipCountries().then(response => fetchCountriesCallback(response, 'setCitizenshipCountries'))
    }
  }, [i18n.language, isInitialised])
  /**
   * @returns {Promise<WhitelabelConfigCommon & AdvancedConfig>}
   */
  const fetchWL = () => {
    const {
      utm_campaign: utmCampaign,
      dept_code: deptCode,
      fund_id: fundId,
      broker_group: brokerGroup
    } = queryString.parse(location.search)

    if (shouldFetchConfig(whitelabel.timestamp)) {
      return DataProvider.get(WHITELABEL_CONFIG_API_PATH)
        .then((config) => convertWlToStore(config))
        .then((convertedWL) => {
          return getDepartmentContacts({ utmCampaign, deptCode, fundId, brokerGroup })
            .then((contacts) => {
              const filteredContacts = {}
              Object.keys(contacts).map(key => {
                const item = contacts[key]
                if (!isNil(item) && !isEmpty(item)) {
                  Object.assign(filteredContacts, { [key]: item })
                }
                return null
              })
              return ({ ...convertedWL, ...filteredContacts, whiteLabelURL: contacts.site || convertedWL.whiteLabelURL })
            })
        })
    }
    return Promise.resolve(whitelabel)
  }

  const dispatchSetValidation = (extFields, mobileVerificationEnabled) => {
    const validation = createStoreValidation(
      [...extFields, ...baseFldsDescriptionListTier1, ...baseFldsDescriptionListTier2],
      mobileVerificationEnabled
    )
    dispatch({
      type: 'setValidation',
      validation
    })
  }

  return whitelabel.whiteLabelName && !isEmpty(validation.tier1) && countries?.length > 0 && (
    <div className={styles.page}>
      <HeadTags />
      <Header />
      <main className={styles.pageContent}>
        <RouterComponent />
      </main>
      <Footer />
    </div>
  )
}
